import type { Pagination } from "./pagination"

interface PagedResponse {
  pagination: Pagination
}

const MAX_PAGES = 1000

/**
 * Drains a cursor-paginated list endpoint. `page(cursor)` fetches one page
 * (cursor is undefined for the first); `select` pulls the collection out of the
 * response. Loops until the backend reports no more pages.
 */
export async function fetchAll<R extends PagedResponse, T>(
  page: (cursor?: string) => Promise<R>,
  select: (response: R) => T[]
): Promise<T[]> {
  const all: T[] = []
  let cursor: string | undefined
  let pages = 0

  do {
    const response = await page(cursor)
    all.push(...select(response))
    cursor = response.pagination.next_cursor ?? undefined
    pages += 1
    if (cursor && pages >= MAX_PAGES) {
      throw new Error("fetchAll exceeded the maximum page count")
    }
  } while (cursor)

  return all
}
